import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrganizationId } from '@app/utils/hooks';
import { Chapter, SprintEntity, useSprintProductReportsBySprint } from '@data';

import { teamAt, unratedHoursBySprint, type VersionTeam } from './budget';
import { readFractionalHours, readRoster } from './fractional';
import { budgetCosts, spendSeries, type SpendSeries } from './spendSeries';
import { readBudget } from './budget';
import { readYearDiscounts } from './discounts';
import { useSettledSprints } from './useSettledSprints';
import { fixedChargeIn } from './pricing';
import { retentionLine } from './retention';
import { useYearChoice } from './yearChoice';
import { activeMemberIds } from './activeTeam';

export interface YearSpend {
  year: string;
  /** The closed sprints, oldest first: the chart's bars. */
  sprints: SprintEntity[];
  /** By chapter, before any discount: a discount comes off a sprint's bill, not off any chapter's share. */
  series: SpendSeries[];
  /** What was taken off each sprint's bill on Costs; absent where nothing was. */
  discountBySprint: Record<string, number>;
  /** What a fixed-price version charged each sprint; absent where the sprint is not fixed price. */
  fixedBySprint: Record<string, number>;
  /** Held back off each sprint after its discount, less what was released into it; absent where neither. */
  retentionBySprint: Record<string, number>;
  /** Hours no rate could price, by sprint: a client's at-cost version, whose costs they are not sent. */
  unratedBySprint: Record<string, number>;
  /** Net of the discounts and the retention, as Costs bills the year. */
  total: number;
  /** People with more than ACTIVE_HOURS on the product over the last ACTIVE_SPRINTS closed sprints. */
  activePeople: number;
  /** The same two per sprint, for the latest sprint's own card. */
  bySprint: Record<string, { hours: number }>;
  /** What each sprint's budget comes to, priced like the spend; null where nothing was budgeted. */
  budgetBySprint: Record<string, number | null>;
  isLoading: boolean;
  /** A sprint's reports could not be read, so every figure above is short of the truth. */
  failed: boolean;
}

export { ACTIVE_HOURS, ACTIVE_SPRINTS } from './activeTeam';

type Entry = { minutes: number };
const hoursOf = (entries: Entry[]): number => entries.reduce((sum, entry) => sum + (entry.minutes ?? 0), 0) / 60;

/** What this year's sprints cost so far. The reports are the ones the other money cards read, so one request. */
export const useYearSpend = (teams: VersionTeam[], chapters: Chapter[], withReports = true): YearSpend => {
  const { t } = useTranslation();
  const { productId = '' } = useParams<{ productId: string }>();
  const { year, yearSprints, isLoading: sprintsLoading } = useYearChoice();
  // Closed sprints only: an open one is half-reported, and a client has not been sent it.
  const settled = useSettledSprints(useOrganizationId(), productId);
  const sprints = useMemo(
    () => yearSprints.filter(sprint => settled.closedSprintIds.includes(sprint.id)),
    [yearSprints, settled.closedSprintIds],
  );
  // A finished product shows no money, so only its year is needed and no sprint's reports are read.
  const reports = useSprintProductReportsBySprint(withReports ? sprints : [], productId);

  const series = useMemo(
    () =>
      spendSeries({
        year,
        sprints,
        reportsBySprint: reports.bySprint,
        teams,
        chapters,
        roster: readRoster(productId, year),
        boughtInHours: sprintId => readFractionalHours('spend', productId, sprintId),
        labels: { unfiled: t('budget.noChapter'), fractional: t('budget.fractional') },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [year, sprints, reports.bySprint, teams, chapters, productId],
  );
  const budgetBySprint = useMemo(
    () =>
      budgetCosts({
        year,
        sprints,
        teams,
        roster: readRoster(productId, year),
        budgetHours: sprintId => ({
          ...(readBudget(productId, sprintId)?.hours ?? {}),
          ...readFractionalHours('budget', productId, sprintId),
        }),
      }),
    [year, sprints, teams, productId],
  );
  const discountBySprint = Object.fromEntries(
    Object.entries(
      readYearDiscounts(
        productId,
        sprints.map(sprint => sprint.id),
      ),
    ).map(([sprintId, discount]) => [sprintId, discount.amount]),
  );
  const fixedBySprint = Object.fromEntries(
    sprints
      .map(sprint => [sprint.id, fixedChargeIn(teamAt(teams, sprint.startDate), sprint.id, yearSprints)] as const)
      .filter((entry): entry is readonly [string, number] => entry[1] !== null),
  );
  const fixed = Object.values(fixedBySprint).reduce((sum, amount) => sum + amount, 0);
  // Held back off what the sprint bills, a fixed price included, after its adjustment.
  const retentionBySprint = Object.fromEntries(
    sprints
      .map((sprint, index) => {
        const net =
          series.reduce((sum, each) => sum + (each.values[index] ?? 0), 0) +
          (fixedBySprint[sprint.id] ?? 0) -
          (discountBySprint[sprint.id] ?? 0);
        return [sprint.id, retentionLine(productId, teamAt(teams, sprint.startDate), sprint.id, net)] as const;
      })
      .filter(([, amount]) => amount !== 0),
  );
  const discounted = [...Object.values(discountBySprint), ...Object.values(retentionBySprint)].reduce(
    (sum, amount) => sum + amount,
    0,
  );
  const total = series.reduce((sum, each) => sum + each.values.reduce((a, b) => a + b, 0), 0) + fixed - discounted;
  const activePeople = activeMemberIds(sprints, reports.bySprint).size;
  const bySprint = Object.fromEntries(
    sprints.map(sprint => {
      const own = reports.bySprint[sprint.id] ?? [];
      return [sprint.id, { hours: hoursOf(own) }];
    }),
  );

  return {
    year,
    sprints,
    series,
    discountBySprint,
    fixedBySprint,
    retentionBySprint,
    unratedBySprint: unratedHoursBySprint(sprints, reports.bySprint, teams, fixedBySprint),
    total,
    activePeople,
    bySprint,
    budgetBySprint,
    isLoading: sprintsLoading || settled.isLoading || reports.isLoading,
    failed: reports.isError || settled.isError,
  };
};
